import { areAllRatingsIncluded } from '@hotjar/feedback';
import { HAS_EMAIL_TYPE } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/HasEmailFilter/HasEmailType';
import { HAS_MESSAGE_TYPE } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/HasFeedbackMessageFilter/HasFeedbackMessageType';
import type { FeedbackResponseFilters } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/ResponsesFilters';
import {
  FROM,
  DATE_FORMAT,
  TO,
  LAST,
} from '@hotjar/feedback/domain/common/Responses/Filters/Query/GlobalFilterQuery/constants';
import { delimit } from '@hotjar/feedback/domain/common/Responses/Filters/Query/queryUtils';
import type { RATING_TYPE } from '@hotjar/feedback/domain/Surveys/Responses/Filters/FilterDataType';
import type { DateRange, DEVICE_TYPE } from '@hotjar/global-filters';
import {
  LargeListFilterOperation,
  RadioTextFilterOperation,
  DATE_RANGE_TYPE,
} from '@hotjar/global-filters';
import { moment } from '@hotjar/insights-common/lib/moment';

function getDateValue(date: DateRange): string {
  switch (date.type) {
    case DATE_RANGE_TYPE.ABSOLUTE:
      return delimit(
        FROM,
        moment(date.from).format(DATE_FORMAT),
        TO,
        moment(date.to).format(DATE_FORMAT),
      );
    case DATE_RANGE_TYPE.RELATIVE:
      return delimit(LAST, date.amount.toString(), date.unit);
    case DATE_RANGE_TYPE.ALL:
      return 'all';
  }
}

function getDeviceValue(devices: DEVICE_TYPE[]) {
  return delimit(...devices.sort());
}

function getCountryValue(selectedValues: string[]) {
  return delimit(...selectedValues.sort());
}

function getRatingValue(ratings: RATING_TYPE[]) {
  if (areAllRatingsIncluded(ratings)) return undefined;
  return delimit(...ratings.map((r) => String(Number(r) + 1)));
}

export function encodeFilterParams(filters: FeedbackResponseFilters): URLSearchParams {
  const params = new URLSearchParams(window.location.search);

  if (filters.date?.value !== undefined) {
    params.set('date', getDateValue(filters.date.value));
  } else {
    params.delete('date');
  }

  if (filters.device?.value !== undefined) {
    params.set('device', getDeviceValue(filters.device.value));
  } else {
    params.delete('device');
  }

  if (
    filters.country?.value !== undefined &&
    filters.country.value.operation === LargeListFilterOperation.IS_ANY_OF_THESE
  ) {
    params.set('country', getCountryValue(filters.country.value.selectedValues));
  } else {
    params.delete('country');
  }

  if (
    filters.country?.value !== undefined &&
    filters.country.value.operation === LargeListFilterOperation.IS_NONE_OF_THESE
  ) {
    params.set('country_not', getCountryValue(filters.country.value.selectedValues));
  } else {
    params.delete('country_not');
  }

  if (filters.emotions?.value !== undefined) {
    const value = getRatingValue(filters.emotions.value);
    if (value === undefined) {
      params.delete('rating');
    } else {
      params.set('rating', value);
    }
  } else {
    params.delete('rating');
  }

  if (
    filters.page?.value !== undefined &&
    filters.page.value.operation === RadioTextFilterOperation.IS
  ) {
    params.set('url', filters.page.value.value);
  } else {
    params.delete('url');
  }

  if (
    filters.page?.value !== undefined &&
    filters.page.value.operation === RadioTextFilterOperation.CONTAINS
  ) {
    params.set('url_contains', filters.page.value.value);
  } else {
    params.delete('url_contains');
  }

  if (
    filters.page?.value !== undefined &&
    filters.page.value.operation === RadioTextFilterOperation.NOT_CONTAINS
  ) {
    params.set('url_not_contains', filters.page.value.value);
  } else {
    params.delete('url_not_contains');
  }

  if (
    filters.page?.value !== undefined &&
    filters.page.value.operation === RadioTextFilterOperation.STARTS_WITH
  ) {
    params.set('url_starts_with', filters.page.value.value);
  } else {
    params.delete('url_starts_with');
  }

  if (
    filters.page?.value !== undefined &&
    filters.page.value.operation === RadioTextFilterOperation.ENDS_WITH
  ) {
    params.set('url_ends_with', filters.page.value.value);
  } else {
    params.delete('url_ends_with');
  }

  if (
    filters.keyword?.value !== undefined &&
    filters.keyword.value.operation === RadioTextFilterOperation.CONTAINS
  ) {
    params.set('keyword_contains', filters.keyword.value.value);
  } else {
    params.delete('keyword_contains');
  }

  if (
    filters.keyword?.value !== undefined &&
    filters.keyword.value.operation === RadioTextFilterOperation.NOT_CONTAINS
  ) {
    params.set('keyword_not_contains', filters.keyword.value.value);
  } else {
    params.delete('keyword_not_contains');
  }

  if (filters.has_email?.value !== undefined && filters.has_email.value !== HAS_EMAIL_TYPE.ANY) {
    params.set('has_email', filters.has_email.value);
  } else {
    params.delete('has_email');
  }

  if (
    filters.has_message?.value !== undefined &&
    filters.has_message.value !== HAS_MESSAGE_TYPE.ANY
  ) {
    params.set('has_message', filters.has_message.value);
  } else {
    params.delete('has_message');
  }

  return params;
}
